import type { Vec2 } from './vec2';
import { heading2, sub2 } from './vec2';
import { polygonSignedArea2 } from './polygon';
import { AREA_EPSILON } from '@/constants/numeric';

/**
 * Half-edge (DCEL) topology for a straight-line planar graph.
 *
 * Half-edge 2*e goes edgesVertices[e][0] -> [1]; half-edge 2*e+1 is its twin.
 * `twin(h) = h ^ 1`.
 *
 * Faces are traced with the classic rule
 *
 *     next(h) = clockwiseNeighbourOf(twin(h))
 *
 * where the neighbour is taken in the counter-clockwise angular ordering of the
 * half-edges leaving a vertex. This walks every face with its interior on the
 * left, so bounded faces come out counter-clockwise (positive signed area) and
 * each connected component's outer face comes out clockwise.
 */
export interface PlanarTopology {
  /** Outgoing half-edge ids at each vertex, sorted counter-clockwise by angle. */
  vertexHalfEdges: number[][];
  /** For each half-edge, the next half-edge of its face cycle. */
  next: Int32Array;
  /** Face id of each half-edge, or -1. */
  halfEdgeFace: Int32Array;
  /** Bounded faces, as counter-clockwise vertex loops. */
  faces: number[][];
  /** The half-edge cycle backing each face, same order as `faces`. */
  faceHalfEdges: number[][];
  /** Cycles that came out clockwise: outer boundary of each component. */
  outerCycles: number[][];
  /** Faces adjacent to each edge: [left, right] as face ids or -1. */
  edgeFaces: [number, number][];
  /** Edge ids incident to each vertex, counter-clockwise. */
  vertexEdges: number[][];
  /** Vertices with no incident edges. */
  isolatedVertices: number[];
}

export const twin = (h: number): number => h ^ 1;
export const halfEdgeToEdge = (h: number): number => h >> 1;

export function halfEdgeOrigin(h: number, edges: readonly (readonly [number, number])[]): number {
  const e = edges[h >> 1];
  return (h & 1) === 0 ? e[0] : e[1];
}

export function halfEdgeTarget(h: number, edges: readonly (readonly [number, number])[]): number {
  const e = edges[h >> 1];
  return (h & 1) === 0 ? e[1] : e[0];
}

export function buildPlanarTopology(
  coords: readonly Vec2[],
  edges: readonly (readonly [number, number])[],
): PlanarTopology {
  const nV = coords.length;
  const nE = edges.length;
  const nH = nE * 2;

  const outgoing: number[][] = Array.from({ length: nV }, () => []);
  for (let e = 0; e < nE; e++) {
    const [a, b] = edges[e];
    if (a === b) continue; // self-loop: not a valid crease
    outgoing[a].push(e * 2);
    outgoing[b].push(e * 2 + 1);
  }

  // Sort outgoing half-edges counter-clockwise by their direction angle.
  const angleOf = (h: number): number => {
    const o = halfEdgeOrigin(h, edges);
    const t = halfEdgeTarget(h, edges);
    return heading2(sub2(coords[t], coords[o]));
  };
  const slotOf = new Int32Array(nH).fill(-1);
  for (let v = 0; v < nV; v++) {
    const list = outgoing[v];
    list.sort((h1, h2) => angleOf(h1) - angleOf(h2));
    for (let i = 0; i < list.length; i++) slotOf[list[i]] = i;
  }

  const next = new Int32Array(nH).fill(-1);
  for (let h = 0; h < nH; h++) {
    if (slotOf[h] < 0) continue;
    const t = twin(h);
    const v = halfEdgeOrigin(t, edges);
    const list = outgoing[v];
    if (list.length === 0) continue;
    const i = slotOf[t];
    // Previous in CCW order == next clockwise neighbour.
    next[h] = list[(i - 1 + list.length) % list.length];
  }

  const halfEdgeFace = new Int32Array(nH).fill(-1);
  const faces: number[][] = [];
  const faceHalfEdges: number[][] = [];
  const outerCycles: number[][] = [];
  const visited = new Uint8Array(nH);

  for (let h0 = 0; h0 < nH; h0++) {
    if (visited[h0] || next[h0] < 0) continue;
    const cycle: number[] = [];
    let h = h0;
    let guard = 0;
    do {
      visited[h] = 1;
      cycle.push(h);
      h = next[h];
      if (h < 0) break;
      if (++guard > nH + 4) break; // topology is broken; bail out rather than hang
    } while (h !== h0);

    const loop = cycle.map((hh) => halfEdgeOrigin(hh, edges));
    const area2 = polygonSignedArea2(loop.map((vi) => coords[vi]));
    if (area2 > AREA_EPSILON) {
      const fid = faces.length;
      for (const hh of cycle) halfEdgeFace[hh] = fid;
      faces.push(loop);
      faceHalfEdges.push(cycle);
    } else {
      outerCycles.push(loop);
    }
  }

  const edgeFaces: [number, number][] = [];
  for (let e = 0; e < nE; e++) {
    edgeFaces.push([halfEdgeFace[e * 2], halfEdgeFace[e * 2 + 1]]);
  }

  const vertexEdges = outgoing.map((list) => list.map(halfEdgeToEdge));
  const isolatedVertices: number[] = [];
  for (let v = 0; v < nV; v++) if (outgoing[v].length === 0) isolatedVertices.push(v);

  return {
    vertexHalfEdges: outgoing,
    next,
    halfEdgeFace,
    faces,
    faceHalfEdges,
    outerCycles,
    edgeFaces,
    vertexEdges,
    isolatedVertices,
  };
}
